import { Routes } from "akademika/akamont/constants/Routes";
import { PartnerDto } from "akademika/akamont/models/PartnerDto";
import JSONModel from "sap/ui/model/json/JSONModel";
import { ValueState } from "sap/ui/core/library";
import {
  ItemInfo,
  UploadSetwithTable$OnActivatedEvent,
  UploadSetwithTable$UploadCompletedEvent,
} from "sap/m/plugins/UploadSetwithTable";
import MessageToast from "sap/m/MessageToast";
import { AttachmentController } from "../sharedControllers/AttachmentController";
import { EmailDto } from "../../models/EmailDto";
import { Button$ClickEvent } from "sap/ui/webc/main/Button";
import ColumnListItem from "sap/m/ColumnListItem";
import { IsUserAdmin } from "../../services/AuthService";

/**
 * @namespace akademika.akamont.pages.partnerDetails
 */
export default class PartnerDetails extends AttachmentController {
  route = Routes.PARTNER_DETAILS;
  parentRoute = Routes.PARTNER_LIST;

  getPartnerDetailsUri = "api/Partner/GetPartnerDetails/{id}";
  deletePartnerUri = "api/Partner/DeletePartner/{id}";
  id = "0";

  uploadAttachmentUri = "api/Partner/UploadAttachment";
  downloadWorkOrderAttachmentUri =
    "api/Partner/DownloadAttachment/{attachmentId}";

  public async onRouteMatched() {
    this.setModel(new JSONModel({ edit: false }), "viewOptions");
    this.setModel(new JSONModel({ value: IsUserAdmin() }), 'isUserAdmin');

    this.id = this.routeParams.id;
    await this.refreshPageData();
  }

  private async getPartnerOptions() {
    await this.fetchModel("api/Partner/GetPartnerSelectData", "partnerOptions");
  }

  private async getPartnerDetails() {
    const url = this.getPartnerDetailsUri.replace("{id}", this.id);
    await this.fetchModel(url);

    const partnerEmails = this.getView()
      ?.getModel()
      ?.getProperty("/partnerEmails");
    const emailIds = partnerEmails.map((e: { emailId: number }) => e.emailId);
    (this.getView()?.getModel() as JSONModel).setProperty(
      "/partnerEmailIds",
      emailIds
    );
  }

  public onEditPartner() {
    this.hideMessage();
    this.getModel("viewOptions").setProperty("/edit", true);
  }

  public async onCancelEdit() {
    this.getModel("viewOptions").setProperty("/edit", false);
    await this.getPartnerDetails();
  }

  public async onSavePartner() {
    this.triggerUploadAttachments();
    MessageToast.show(this.translate("UploadingAttachments"));
    await Promise.all(this._pendingUploadsQueue);
    MessageToast.show(this.translate("AttachmentsUploaded"));
    const requestData = this.getModelData<PartnerDto>();
    await this.saveDataAndShowMessage("api/Partner/EditPartner", {
      body: requestData,
    });

    const model = this.getModel("viewOptions");
    model.setProperty("/edit", false);
    await this.getPartnerDetails();
  }

  public async onDeletePartner() {
    this.openSimpleDialog({
      title: "deletePartner",
      message: "deletePartnerQuestion",
      state: ValueState.Warning,
      type: "Confirm",
      confirmWithComment: false,
      confirmAction: async () => {
        const uri = this.deletePartnerUri.replace("{id}", this.id);
        await this.post(uri);
        this.navTo(this.parentRoute);
      },
      cancelAction: async () => await this.getPartnerDetails(),
    });
  }

  public initUploadPlugin(event: UploadSetwithTable$OnActivatedEvent) {
    const baseUrl = this.getApiService().getBaseUrl();
    const uploadEndpoint = `${baseUrl}${this.uploadAttachmentUri}`;
    const uploadPlugin = event.getParameter("oPlugin");
    uploadPlugin?.setUploadUrl(uploadEndpoint);
    console.log("Upload plugin initialized.");
  }

  public attachmentValidationHandler(itemInfo: ItemInfo) {
    return this.handleAttachmentValidation(itemInfo);
  }

  public onAttachmentUploadCompleted(
    event: UploadSetwithTable$UploadCompletedEvent
  ) {
    this.handleUploadCompleted(event);
  }

  public async downloadAttachment(id: number | string) {
    const uri = this.downloadWorkOrderAttachmentUri.replace(
      "{attachmentId}",
      id.toString()
    );
    this.getApiService().downloadFile(uri);
  }

  public addEmail() {
    const model = this.getModel() as JSONModel;
    const emailValue = model.getProperty("/newEmail");

    if (!emailValue) return;

    const emails = model.getProperty("/partnerEmails") as EmailDto[];
    const newEmailObject: EmailDto = {
      active: true,
      value: emailValue,
    };
    emails.push(newEmailObject);
    model.refresh();
    model.setProperty("/newEmail", "");
  }

  public onDeleteEmail(event: Button$ClickEvent) {
    const source = event.getSource().getParent() as ColumnListItem;
    const bindingPath = source.getBindingContext()?.getPath();
    const model = this.getModel() as JSONModel;
    if (bindingPath) {
      const item = model.getProperty(bindingPath);
      const allEmails = model.getProperty("/partnerEmails") as EmailDto[];
      const filteredEmails = allEmails.filter((e) => e.value != item.value);
      model.setProperty("/partnerEmails", filteredEmails);
    }
  }

  public async refreshPageData() {
    await this.getPartnerOptions();
    await this.getPartnerDetails();
  }
}
